import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  ZAxis,
} from 'recharts';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

const RiskRewardScatterPlot = ({ sectors }) => {
  const { t } = useTranslation();
  if (!sectors || sectors.length === 0) {
    return null;
  }

  // Prepare scatter data
  const scatterData = sectors.map(sector => ({
    name: sector.valuation_sector,
    risk: parseFloat(sector.avg_val_score || 50), // Use valuation as risk proxy
    reward: parseFloat(sector.tailwind_score || sector.final_score || 0), // Use tailwind as reward proxy
    valuation: parseFloat(sector.avg_val_score || 50),
    finalScore: parseFloat(sector.final_score || 0),
  }));

  // Calculate averages for reference lines
  const avgRisk = scatterData.reduce((sum, d) => sum + d.risk, 0) / scatterData.length;
  const avgReward = scatterData.reduce((sum, d) => sum + d.reward, 0) / scatterData.length;

  // Color based on final score
  const getColor = (finalScore) => {
    if (finalScore >= 70) return '#11998e'; // High - Green
    if (finalScore >= 60) return '#667eea'; // Good - Blue
    if (finalScore >= 50) return '#ffa726'; // Neutral - Orange
    return '#ff6b6b'; // Low - Red
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {data.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.riskReward.tailwindReward')}: <strong>{data.reward.toFixed(1)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.riskReward.valuationRisk')}: <strong>{data.risk.toFixed(1)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.riskReward.valScore')}: <strong>{data.valuation.toFixed(1)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.riskReward.finalScore')}: <strong>{data.finalScore.toFixed(1)}</strong>
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TrendingUpIcon sx={{ fontSize: 24, mr: 1.5, color: '#667eea' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('dashboard.riskReward.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.riskReward.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box>
          <ResponsiveContainer width="100%" height={400} minWidth={400} minHeight={400}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                type="number"
                dataKey="risk"
                name={t('dashboard.riskReward.valuationRisk')}
                label={{ value: t('dashboard.riskReward.valuationRisk'), position: 'insideBottom', offset: -10, fill: '#666' }}
                stroke="#666"
              />
              <YAxis
                type="number"
                dataKey="reward"
                name={t('dashboard.riskReward.tailwindReward')}
                label={{ value: t('dashboard.riskReward.tailwindReward'), angle: -90, position: 'insideLeft', fill: '#666' }}
                stroke="#666"
              />
              <ZAxis type="number" dataKey="valuation" range={[100, 1000]} />
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
              
              {/* Reference lines for quadrants */}
              <ReferenceLine
                x={avgRisk}
                stroke="#999"
                strokeDasharray="5 5"
                label={{ value: t('dashboard.riskReward.avgRisk'), position: 'top', fill: '#666' }}
              />
              <ReferenceLine
                y={avgReward}
                stroke="#999"
                strokeDasharray="5 5"
                label={{ value: t('dashboard.riskReward.avgReward'), position: 'right', fill: '#666' }}
              />

              <Scatter name="Sectors" data={scatterData}>
                {scatterData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColor(entry.finalScore)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>

          {/* Legend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label={t('dashboard.riskReward.highScore')}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#11998e', color: '#11998e' }}
            />
            <Chip
              label={t('dashboard.riskReward.goodScore')}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#667eea', color: '#667eea' }}
            />
            <Chip
              label={t('dashboard.riskReward.neutral')}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#ffa726', color: '#ffa726' }}
            />
            <Chip
              label={t('dashboard.riskReward.lowScore')}
              size="small"
              variant="outlined"
              sx={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RiskRewardScatterPlot;

